import { useCallback, useEffect, useState } from "react";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, type Kayttaja, type Kohde, type TgPortit, type TgPortti } from "./api";
import { Tilasana, type Tilalaji } from "./Tila";
import { naytaPvm, naytaPvmKello } from "./pvm";
import { t, esim, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════════
   Suunnittelu-moduuli, vaihe 1: kohteen portit (TG) (254, T625, Toni 21.10.2026 klo 06.55).

   Portit ovat kohdekohtaisia (järjestys, tunnus, nimi, määräaika, tila) ja piirtyvät vaakanuolena järjestyksessä.
   Hyväksyntä ja määräajan siirto ovat omia oikeuksiaan (tg_hyvaksynta, tg_maaraaika) — palvelin kertoo ne
   vastauksessa, eikä näkymä päättele niitä roolista. Jokainen hyväksyntä ja siirto näkyy portin tapahtumalokissa
   (kuka, milloin, vanha ja uusi päivä, peruste).

   Tila ei ole pelkkä väri (Tila.tsx): hyväksytty = muste ja sana, avoin = sana, myöhässä = varoitus ja sana.
   Vaihe 2 (tehtävät, joiden tekemättömyys estää seuraavan portin) tulee omana eränään.
   ═══════════════════════════════════════════════════════════════ */

function tanaan(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function portinTila(p: TgPortti, paiva: string): { laji: Tilalaji; sana: string; avain: "hyvaksytty" | "myohassa" | "avoin" } {
  if (p.tila === "hyvaksytty") return { laji: "kunnossa", sana: t("tg.tila.hyvaksytty"), avain: "hyvaksytty" };
  if (p.maaraaika < paiva) return { laji: "varoitus", sana: t("tg.tila.myohassa"), avain: "myohassa" };
  return { laji: "ei_mitattu", sana: t("tg.tila.avoin"), avain: "avoin" };
}

export default function Portit({ kayttaja: _kayttaja }: { kayttaja: Kayttaja }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohdeId, setKohdeId] = useState<number | "">("");
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohdeId, setKohdeId, { kaikkiSallittu: false });
  const [data, setData] = useState<TgPortit | null>(null);
  const [valittu, setValittu] = useState<number | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tyo, setTyo] = useState(false);
  const [uusi, setUusi] = useState({ tunnus: "", nimi: "", maaraaika: "" });
  const [siirto, setSiirto] = useState({ maaraaika: "", peruste: "" });
  const [hyvPeruste, setHyvPeruste] = useState("");
  const paiva = tanaan();

  useEffect(() => {
    void (async () => {
      try {
        const k = await api.kohteet();
        setKohteet(k);
        const eka = k[0];
        if (eka) setKohdeId((v) => (v === "" ? eka.id : v));   // yläpalkin kohde ei korvaudu oletuksella
      } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
    })();
  }, []);

  const viimeisin = useViimeisin();
  const lataa = useCallback(async () => {
    if (kohdeId === "") return;
    const tuore = viimeisin();
    try {
      const d = await api.tgPortit(kohdeId);
      if (!tuore()) return;
      setData(d); setVirhe(null);
      setValittu((v) => (v != null && d.portit.some((p) => p.id === v) ? v
        : (d.portit.find((p) => p.tila === "avoin") ?? d.portit[0])?.id ?? null));
    } catch (e) { if (!tuore()) return; setData(null); setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
  }, [kohdeId, viimeisin]);
  useEffect(() => { void lataa(); }, [lataa]);

  const portti = data?.portit.find((p) => p.id === valittu) ?? null;
  useEffect(() => { setSiirto({ maaraaika: portti?.maaraaika ?? "", peruste: "" }); setHyvPeruste(""); }, [portti?.id, portti?.maaraaika]);

  async function aja(f: () => Promise<unknown>, onnistui: string) {
    if (tyo) return;
    setTyo(true); setIlmoitus(null); setVirhe(null);
    try { await f(); setIlmoitus(onnistui); await lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tk.tallennus_virhe")); }
    finally { setTyo(false); }
  }

  const portit = data?.portit ?? [];
  const hyvaksyttyja = portit.filter((p) => p.tila === "hyvaksytty").length;
  const seuraava = portit.find((p) => p.tila === "avoin");
  const tapahtumat = (data?.tapahtumat ?? []).filter((x) => x.portti_id === valittu);
  // Vaihe 2 (255): tekemätön tehtävä estää portin ja seuraavat. Sama sääntö kuin palvelukerroksessa (suunnittelu.ts keskenTehtavat):
  // portti on lukittu, jos jollain aiemmalla portilla on tekemättömiä tehtäviä. Palvelin on portti; tämä näyttää syyn etukäteen.
  const tehtavat = (pid: number) => (data?.tehtavat ?? []).filter((x) => x.portti_id === pid);
  const kesken = (pid: number) => tehtavat(pid).filter((x) => !x.tehty).length;
  const lukitsijat = (pid: number) => {
    const i = portit.findIndex((p) => p.id === pid);
    return portit.slice(0, Math.max(0, i)).filter((p) => kesken(p.id) > 0).map((p) => `${p.tunnus} (${kesken(p.id)})`);
  };
  const [uusiTehtava, setUusiTehtava] = useState("");

  return (
    <section className="panel" data-nakyma="portit">
      <div className="panel__head"><h2>{t("valikko.portit")}</h2></div>
      <p className="vihje">{t("tg.selite")}</p>
      {!(ylapalkissa && ylaValittu != null) && (
        <div className="rivi">
          <label>{t("pu.kohde")}
            <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value === "" ? "" : Number(e.target.value))} data-tg-kohde>
              <option value="">{t("hl.valitse_viivat")}</option>
              {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} {k.nimi}</option>)}
            </select>
          </label>
        </div>
      )}
      {virhe && <p className="virhe" role="alert" data-tg-virhe>{virhe}</p>}
      {ilmoitus && <p className="ilmoitus" role="status" data-tg-ilmoitus>{ilmoitus}</p>}

      {data && (
        <>
          <p data-tg-yhteenveto data-rivit={portit.length}>
            <b>{data.kohde.tunnus}</b>{" · "}{t("tg.lapi", { n: hyvaksyttyja, m: portit.length })}
            {" · "}{t("tg.seuraava")}{" "}
            {seuraava ? <b>{seuraava.tunnus} {naytaPvm(seuraava.maaraaika)}</b> : "—"}
          </p>

          {portit.length === 0 ? (
            <p className="vihje" data-tg-tyhja>{data.oikeudet.luonti ? t("tg.ei_portteja_luo") : t("tg.ei_portteja")}</p>
          ) : (
            <div style={{ overflowX: "auto", padding: "8px 0 4px" }}>
              <ol aria-label={t("tg.aikajana")} data-tg-nuoli
                  style={{ listStyle: "none", margin: 0, padding: "0 24px 0 0", position: "relative", display: "flex",
                           minWidth: `${portit.length * 120}px` }}>
                <li aria-hidden="true" style={{ position: "absolute", left: 0, right: 14, top: 23, height: 3, background: "var(--muste)" }} />
                <li aria-hidden="true" style={{ position: "absolute", right: 0, top: 16, width: 0, height: 0,
                  borderTop: "8.5px solid transparent", borderBottom: "8.5px solid transparent", borderLeft: "16px solid var(--muste)" }} />
                {portit.map((p) => {
                  const ti = portinTila(p, paiva);
                  const on = p.id === valittu;
                  const reuna = ti.avain === "myohassa" ? "var(--varoitus)" : "var(--muste)";
                  return (
                    <li key={p.id} style={{ flex: "1 1 0", minWidth: 110, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, position: "relative" }}>
                      <button type="button" onClick={() => setValittu(p.id)} aria-pressed={on}
                              aria-label={`${p.tunnus} ${p.nimi}, ${naytaPvm(p.maaraaika)}, ${ti.sana}`}
                              data-tg-portti={p.tunnus} data-tg-tila={ti.avain}
                              style={{ width: 48, height: 48, borderRadius: "50%", cursor: "pointer", padding: 0,
                                       fontFamily: "var(--mono)", fontSize: 13, fontWeight: 600,
                                       border: `3px solid ${reuna}`,
                                       background: ti.avain === "hyvaksytty" ? "var(--muste)" : "var(--kentta-tausta, #fff)",
                                       color: ti.avain === "hyvaksytty" ? "#fff" : reuna,
                                       boxShadow: on ? "0 0 0 4px var(--korostus)" : "none" }}>
                        {ti.avain === "hyvaksytty" ? "✓" : p.tunnus}
                      </button>
                      <span style={{ fontSize: 14, fontWeight: 600, textAlign: "center" }}>{p.tunnus} {p.nimi}</span>
                      <span style={{ fontFamily: "var(--mono)", fontSize: 14 }} data-tg-maaraaika={p.maaraaika}>{naytaPvm(p.maaraaika)}</span>
                      <Tilasana laji={ti.laji}>{ti.sana}</Tilasana>
                      {tehtavat(p.id).length > 0 && (
                        <span style={{ fontSize: 14 }} data-tg-tehtavia={`${tehtavat(p.id).length - kesken(p.id)}/${tehtavat(p.id).length}`}>
                          {t("tg.tehtavia", { n: tehtavat(p.id).length - kesken(p.id), m: tehtavat(p.id).length })}
                        </span>
                      )}
                      {p.tila === "avoin" && lukitsijat(p.id).length > 0 && <Tilasana laji="ei_mitattu">{t("tg.lukittu")}</Tilasana>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {portti && (() => {
            const ti = portinTila(portti, paiva);
            return (
              <div className="panel" data-tg-valittu={portti.tunnus} style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>{portti.tunnus} · {portti.nimi}</h3>
                <p>
                  {t("tg.maaraaika")}: <b>{naytaPvm(portti.maaraaika)}</b>{" · "}<Tilasana laji={ti.laji}>{ti.sana}</Tilasana>
                  {portti.tila === "hyvaksytty" && (
                    <>{" · "}{t("tg.hyvaksyi", { kuka: portti.hyvaksyja ?? "", milloin: naytaPvmKello(portti.hyvaksytty) ?? "" })}</>
                  )}
                </p>

                {/* Vaihe 2 (255): tehtävät. Lukitun portin tehtäviä ei kuitata; hyväksytyn portin tehtävät on jäädytetty. */}
                {(() => {
                  const lista = tehtavat(portti.id);
                  const lukot = lukitsijat(portti.id);
                  const voiKuitata = data.oikeudet.kuittaus && portti.tila === "avoin" && lukot.length === 0;
                  return (
                    <div data-tg-tehtavat={portti.tunnus} data-rivit={lista.length}>
                      <h4 style={{ marginBottom: 6 }}>{t("tg.tehtavat")}{lista.length > 0 ? ` · ${t("tg.tehtavia", { n: lista.length - kesken(portti.id), m: lista.length })}` : ""}</h4>
                      {portti.tila === "avoin" && lukot.length > 0 && (
                        <p role="status" data-tg-lukittu style={{ margin: "0 0 8px" }}>
                          <Tilasana laji="varoitus">{t("tg.lukittu")}</Tilasana>{" "}{t("tg.lukittu_syy", { portit: lukot.join(", ") })}
                        </p>
                      )}
                      {lista.length === 0 ? <p className="vihje">{t("tg.ei_tehtavia")}</p> : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                          {lista.map((x) => (
                            <li key={x.id}>
                              <label style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }} data-tg-tehtava={x.teksti} data-tg-tehty={x.tehty ? "1" : "0"}>
                                <input type="checkbox" checked={!!x.tehty} disabled={!voiKuitata || tyo} style={{ width: 20, height: 20 }}
                                       onChange={() => void aja(() => api.tgKuittaa(x.id, !x.tehty), x.tehty ? t("tg.tehtava_auki_ok") : t("tg.tehtava_tehty_ok"))} />
                                <span style={{ textDecoration: x.tehty ? "line-through" : "none" }}>{x.teksti}</span>
                                {x.tehty && <span className="vihje">{t("tg.tehtava_tehnyt", { kuka: x.tekija ?? "", milloin: naytaPvmKello(x.tehty) ?? "" })}</span>}
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                      {portti.tila === "avoin" && data.oikeudet.luonti && (
                        <form className="lomake__kentat" style={{ alignItems: "flex-end", marginTop: 8 }} data-tg-uusi-tehtava
                              onSubmit={(e) => { e.preventDefault(); const tx = uusiTehtava.trim(); if (!tx) return;
                                void aja(async () => { await api.tgLisaaTehtava(portti.id, tx); setUusiTehtava(""); }, t("tg.tehtava_lisatty_ok")); }}>
                          <label className="kasvaa">{t("tg.uusi_tehtava")}
                            <input value={uusiTehtava} onChange={(e) => setUusiTehtava(e.target.value)} maxLength={300} data-tg-uusi-tehtava-teksti />
                          </label>
                          <button type="submit" className="nappi" disabled={tyo || !uusiTehtava.trim()} data-tg-lisaa-tehtava>{t("tg.lisaa_tehtava")}</button>
                        </form>
                      )}
                    </div>
                  );
                })()}

                {portti.tila === "avoin" && data.oikeudet.hyvaksynta && (() => {
                  const este = lukitsijat(portti.id).length > 0 ? t("tg.lukittu_syy", { portit: lukitsijat(portti.id).join(", ") })
                    : kesken(portti.id) > 0 ? t("tg.tehtavia_kesken", { n: kesken(portti.id) }) : null;
                  return (
                  <div className="lomake__kentat" style={{ alignItems: "flex-end" }}>
                    <label className="kasvaa">{t("tg.peruste_valinnainen")}
                      <input value={hyvPeruste} onChange={(e) => setHyvPeruste(e.target.value)} maxLength={1000} data-tg-hyvaksynta-peruste />
                    </label>
                    {este && <span role="status" data-tg-hyvaksynta-este><Tilasana laji="varoitus">{este}</Tilasana></span>}
                    <button type="button" className="nappi nappi--paa" disabled={tyo || !!este} data-tg-hyvaksy
                            onClick={() => void aja(() => api.tgHyvaksy(portti.id, hyvPeruste.trim() || null), t("tg.hyvaksytty_ok", { tunnus: portti.tunnus }))}>
                      {t("tg.hyvaksy")}
                    </button>
                  </div>
                  );
                })()}
                {portti.tila === "avoin" && data.oikeudet.maaraaika && (
                  <div className="lomake__kentat" style={{ alignItems: "flex-end" }}>
                    <label>{t("tg.uusi_maaraaika")}
                      <input type="date" value={siirto.maaraaika} onChange={(e) => setSiirto((s) => ({ ...s, maaraaika: e.target.value }))} data-tg-siirto-pvm />
                    </label>
                    <label className="kasvaa">{t("tg.peruste_valinnainen")}
                      <input value={siirto.peruste} onChange={(e) => setSiirto((s) => ({ ...s, peruste: e.target.value }))} maxLength={1000} data-tg-siirto-peruste />
                    </label>
                    <button type="button" className="nappi" data-tg-siirra
                            disabled={tyo || !siirto.maaraaika || siirto.maaraaika === portti.maaraaika}
                            onClick={() => void aja(() => api.tgSiirra(portti.id, siirto.maaraaika, siirto.peruste.trim() || null), t("tg.siirretty_ok", { tunnus: portti.tunnus }))}>
                      {t("tg.siirra")}
                    </button>
                  </div>
                )}
                {portti.tila === "avoin" && !data.oikeudet.hyvaksynta && !data.oikeudet.maaraaika && (
                  <p className="vihje" data-tg-ei-oikeutta>{t("tg.ei_oikeutta")}</p>
                )}

                <h4>{t("tg.loki")}</h4>
                <table className="taulu" data-tg-loki data-rivit={tapahtumat.length}>
                  <thead><tr><th>{t("tg.loki.milloin")}</th><th>{t("tg.loki.kuka")}</th><th>{t("tg.loki.tapahtuma")}</th><th>{t("tg.loki.paivat")}</th><th>{t("tg.loki.peruste")}</th></tr></thead>
                  <tbody>
                    {tapahtumat.map((x) => (
                      <tr key={x.id} data-tg-tapahtuma={x.laji}>
                        <td>{naytaPvmKello(x.milloin)}</td>
                        <td>{x.kuka}</td>
                        <td>{t(x.laji === "luonti" ? "tg.laji.luonti" : x.laji === "siirto" ? "tg.laji.siirto" : "tg.laji.hyvaksynta")}</td>
                        <td>{x.laji === "siirto" ? `${naytaPvm(x.vanha_pvm)} → ${naytaPvm(x.uusi_pvm)}`
                             : x.laji === "luonti" ? naytaPvm(x.uusi_pvm) : naytaPvm(x.vanha_pvm)}</td>
                        <td>{x.peruste ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {data.oikeudet.luonti && (
            <form className="lomake__kentat" style={{ marginTop: 16, alignItems: "flex-end" }} data-tg-uusi
                  onSubmit={(e) => { e.preventDefault(); if (kohdeId === "") return;
                    void aja(async () => { await api.tgLuoPortti({ kohde_id: kohdeId, ...uusi }); setUusi({ tunnus: "", nimi: "", maaraaika: "" }); },
                             t("tg.lisatty_ok", { tunnus: uusi.tunnus.trim() })); }}>
              <label>{t("tg.tunnus")}
                <input value={uusi.tunnus} onChange={(e) => setUusi((s) => ({ ...s, tunnus: e.target.value }))} maxLength={20} required
                       placeholder={esim(`TG${portit.length}`)} style={{ width: 120 }} data-tg-uusi-tunnus />
              </label>
              <label className="kasvaa">{t("tg.nimi")}
                <input value={uusi.nimi} onChange={(e) => setUusi((s) => ({ ...s, nimi: e.target.value }))} maxLength={200} required data-tg-uusi-nimi />
              </label>
              <label>{t("tg.maaraaika")}
                <input type="date" value={uusi.maaraaika} onChange={(e) => setUusi((s) => ({ ...s, maaraaika: e.target.value }))} required data-tg-uusi-pvm />
              </label>
              <button type="submit" className="nappi nappi--paa" disabled={tyo} data-tg-lisaa>{t("tg.lisaa")}</button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
